/**
 * WithdrawalBreakdownView / WithdrawalResultView — the acceptance-withdrawal
 * dialog's pre-commit breakdown and post-commit result (task 4.3). Extracted
 * out of withdraw-acceptance-dialog.tsx to keep that file under this repo's
 * 400-line limit — mirrors lock-breakdown-view.tsx's own extraction.
 *
 * Renders the withdrawal preview's two outcomes (releasable, unresolvable)
 * as distinct, separately-labelled groups, each with a distinct-nośnik count
 * and a collapsible entry list, reusing the shared
 * OutcomeResultBucket/OutcomeResultEntryRow presentation task 4.1 built.
 *
 * WithdrawalResultView replays the confirmed breakdown snapshot rather than
 * reading the withdrawal endpoint's own response
 * (RejectVersionResponse.reservationRelease): that shape identifies each
 * entry by reservationId only, with no mediaUnitId — a pre-existing contract
 * this change's Non-Goals leave untouched ("No change to what any of the
 * four actions does once committed"). The withdrawal and its preview are
 * required to agree on outcome (offer-versioning-diff / "a divergence is a
 * defect"), so replaying the snapshot taken immediately before the request
 * was sent is correct for the well-behaved case this dialog is a UI for. A
 * genuine concurrent-modification divergence is asserted at the API layer
 * only (offer-versioning-diff / "The committed result overrides a breakdown
 * another caller invalidated", test boundary: api).
 *
 * Traceability: offer-versioning-diff / Acceptance-withdrawal preview;
 * Internal acceptance-withdrawal control on the Versions tab.
 */
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle } from 'lucide-react';
import type { BadgeProps } from '@ooh/ui/components/badge';
import { StatCard } from '@ooh/ui/components/stat-card';
import type { WithdrawalPreviewItem, WithdrawalPreviewResponse } from '@ooh/contracts';
import {
  BreakdownGroupChip,
  BreakdownPreviewState,
  distinctUnitCount,
  OutcomeResultBucket,
  OutcomeResultEntryRow,
  type LooseT,
  type OutcomeResultUnitLookup,
} from '../../reservations/components/outcome-result-buckets';

/** Display order for the withdrawal preview's outcome groups — an outcome
 * with no entries is omitted rather than shown as a zero count
 * (action-breakdown / "Where an outcome applies to no entry, its count
 * SHALL be omitted"). */
const WITHDRAWAL_OUTCOME_ORDER: WithdrawalPreviewItem['outcome'][] = ['releasable', 'unresolvable'];

const BREAKDOWN_LABEL_KEY: Record<WithdrawalPreviewItem['outcome'], string> = {
  releasable: 'workspace.versions.withdrawAcceptance.dialog.breakdown.releasableLabel',
  unresolvable: 'workspace.versions.withdrawAcceptance.dialog.breakdown.unresolvableLabel',
};

const RESULT_LABEL_KEY: Record<WithdrawalPreviewItem['outcome'], string> = {
  releasable: 'workspace.versions.withdrawAcceptance.result.releasedLabel',
  unresolvable: 'workspace.versions.withdrawAcceptance.result.failedLabel',
};

const BADGE_VARIANT: Record<WithdrawalPreviewItem['outcome'], BadgeProps['variant']> = {
  releasable: 'success',
  unresolvable: 'destructive',
};

interface WithdrawalOutcomeGroup {
  outcome: WithdrawalPreviewItem['outcome'];
  entries: WithdrawalPreviewItem[];
}

function groupWithdrawalItems(items: WithdrawalPreviewResponse): WithdrawalOutcomeGroup[] {
  return WITHDRAWAL_OUTCOME_ORDER.map((outcome) => ({
    outcome,
    entries: items.filter((item) => item.outcome === outcome),
  })).filter((group) => group.entries.length > 0);
}

/**
 * The preview's only unresolvable case today is a sold reservation
 * (OfferVersionsService.getWithdrawalPreview) — a fixed translated message,
 * never the raw `reason` string the server sends (action-breakdown / "SHALL
 * NOT print a raw error identifier"), same convention as release-rejected's
 * releaseBreakdownUnresolvableDetail.
 */
function withdrawalUnresolvableDetail(t: LooseT): string {
  return t('workspace.versions.withdrawAcceptance.dialog.breakdown.unresolvableSoldReason');
}

export interface WithdrawalBreakdownPreview {
  isLoading: boolean;
  isError: boolean;
  data: WithdrawalPreviewResponse | undefined;
  refetch: () => void;
}

export function WithdrawalBreakdownView({
  preview,
  lookup,
}: {
  preview: WithdrawalBreakdownPreview;
  lookup: OutcomeResultUnitLookup;
}) {
  const { t } = useTranslation('offers');
  const looseT = t as LooseT;
  const unknownUnitLabel = t('workspace.versions.withdrawAcceptance.result.unknownUnit');

  const items = preview.data ?? [];
  const groups = groupWithdrawalItems(items);

  return (
    <BreakdownPreviewState
      preview={preview}
      testIdPrefix="withdraw-breakdown"
      loadingLabel={t('workspace.versions.withdrawAcceptance.dialog.breakdown.loading')}
      errorLabel={t('workspace.versions.withdrawAcceptance.dialog.breakdown.error')}
      retryLabel={t('workspace.versions.withdrawAcceptance.dialog.breakdown.retryBtn')}
      // A version whose nośniki were never locked previews as empty — say so
      // plainly, and leave the withdrawal available (offer-versioning-diff /
      // "A version whose nośniki were never locked says so plainly").
      isEmpty={items.length === 0}
      emptyLabel={t('workspace.versions.withdrawAcceptance.dialog.breakdown.empty')}
    >
      <div className="space-y-2" data-testid="withdraw-breakdown">
        <div className="flex flex-wrap gap-2" data-testid="withdraw-breakdown-groups">
          {groups.map((group) => (
            <BreakdownGroupChip
              key={group.outcome}
              testId={`withdraw-breakdown-group-${group.outcome}`}
              variant={BADGE_VARIANT[group.outcome]}
              label={looseT(BREAKDOWN_LABEL_KEY[group.outcome])}
              count={distinctUnitCount(group.entries)}
            />
          ))}
        </div>

        {groups.map((group) => (
          <OutcomeResultBucket<WithdrawalPreviewItem>
            key={group.outcome}
            trigger={
              <span className="text-sm font-medium">
                {looseT(BREAKDOWN_LABEL_KEY[group.outcome])}
              </span>
            }
            entries={group.entries}
            emptyLabel=""
            testIdPrefix="withdraw-breakdown-entry"
            renderEntry={(entry) => (
              <OutcomeResultEntryRow
                unit={lookup.get(entry.mediaUnitId)}
                dateFrom={entry.dateFrom}
                dateTo={entry.dateTo}
                detail={
                  entry.outcome === 'unresolvable'
                    ? withdrawalUnresolvableDetail(looseT)
                    : undefined
                }
                unknownUnitLabel={unknownUnitLabel}
              />
            )}
          />
        ))}
      </div>
    </BreakdownPreviewState>
  );
}

/** Post-commit result — see this module's own doc comment for why it
 * replays the confirmed breakdown snapshot instead of the withdrawal
 * response's reservationRelease. */
export function WithdrawalResultView({
  committedItems,
  lookup,
}: {
  committedItems: WithdrawalPreviewResponse;
  lookup: OutcomeResultUnitLookup;
}) {
  const { t } = useTranslation('offers');
  const looseT = t as LooseT;
  const unknownUnitLabel = t('workspace.versions.withdrawAcceptance.result.unknownUnit');
  const groups = groupWithdrawalItems(committedItems);
  const releasedCount = distinctUnitCount(
    committedItems.filter((item) => item.outcome === 'releasable'),
  );
  const failedCount = distinctUnitCount(
    committedItems.filter((item) => item.outcome === 'unresolvable'),
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <StatCard
          icon={CheckCircle2}
          tone="success"
          label={t('workspace.versions.withdrawAcceptance.result.releasedLabel')}
          value={releasedCount}
          loading={false}
          testId="withdraw-acceptance-released-count"
        />
        <StatCard
          icon={XCircle}
          tone="destructive"
          label={t('workspace.versions.withdrawAcceptance.result.failedLabel')}
          value={failedCount}
          loading={false}
          testId="withdraw-acceptance-failed-count"
        />
      </div>

      <div className="space-y-2">
        {groups.map((group) => (
          <OutcomeResultBucket<WithdrawalPreviewItem>
            key={group.outcome}
            trigger={
              <span className="text-sm font-medium">{looseT(RESULT_LABEL_KEY[group.outcome])}</span>
            }
            entries={group.entries}
            emptyLabel=""
            testIdPrefix="withdraw-acceptance-outcome-entry"
            renderEntry={(entry) => (
              <OutcomeResultEntryRow
                unit={lookup.get(entry.mediaUnitId)}
                dateFrom={entry.dateFrom}
                dateTo={entry.dateTo}
                detail={
                  entry.outcome === 'unresolvable'
                    ? withdrawalUnresolvableDetail(looseT)
                    : undefined
                }
                unknownUnitLabel={unknownUnitLabel}
              />
            )}
          />
        ))}
      </div>
    </div>
  );
}
